import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect
} from 'react'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { ErrorBoundary } from './ErrorBoundary'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAuthorizeUserWithToken } from '@/hooks/use-authorizeWithToken'

interface LoadingContextType {
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  showLoader: (duration?: number) => ReturnType<typeof setTimeout> | void
  hasConnectionError: boolean
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined)

export const useLoading = () => {
  const context = useContext(LoadingContext)
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider')
  }
  return context
}

interface LoadingProviderProps {
  children: React.ReactNode
}

export function LoadingProvider({ children }: LoadingProviderProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [hasConnectionError, setHasConnectionError] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const timeouts = useRef<Set<NodeJS.Timeout>>(new Set())
  const retryCount = useRef(0)
  const isMounted = useRef(true)

  const MAX_RETRIES = 3
  const MAX_DURATION = 3000
  const RECONNECT_INTERVAL = 2000

  const clearTimeouts = useCallback(() => {
    timeouts.current.forEach((timeout) => {
      clearTimeout(timeout)
    })
    timeouts.current.clear()
  }, [])

  const addTimeout = useCallback((timeout: NodeJS.Timeout) => {
    if (isMounted.current) {
      timeouts.current.add(timeout)
    }
    return timeout
  }, [])

  const showLoader = useCallback(
    (duration = 1500) => {
      if (!isMounted.current) return

      setIsLoading(true)
      const timeout = setTimeout(() => {
        if (isMounted.current) {
          setIsLoading(false)
          timeouts.current.delete(timeout)
        }
      }, Math.min(duration, MAX_DURATION))

      return addTimeout(timeout)
    },
    [addTimeout]
  )

  const checkConnection = useCallback(async () => {
    if (!isMounted.current) return

    try {
      //TODO: Commented health check before until the Backend is ready
      // const response = await apiFetch('/v1/health')
      // const data = await response.json()
      const response = { ok: true }
      const data = { status: 'ok', error: '' }
      if (!response.ok || data.status !== 'ok') {
        throw new Error(data.error || 'Server health check failed')
      }

      if (isMounted.current) {
        setHasConnectionError(false)
        setIsReconnecting(false)
        retryCount.current = 0
      }
    } catch (error) {
      if (!isMounted.current) return

      setHasConnectionError(true)

      if (!isReconnecting && retryCount.current < MAX_RETRIES) {
        setIsReconnecting(true)
        retryCount.current++

        const backoffDelay = Math.min(
          RECONNECT_INTERVAL * Math.pow(1.5, retryCount.current),
          15000
        )

        const reconnectTimeout = setTimeout(() => {
          if (isMounted.current) {
            checkConnection()
          }
        }, backoffDelay)

        addTimeout(reconnectTimeout)
      } else if (retryCount.current >= MAX_RETRIES) {
        setIsReconnecting(false)
      }
    }
  }, [isReconnecting, addTimeout])

  useEffect(() => {
    isMounted.current = true

    const setupConnectionCheck = () => {
      if (!isMounted.current) return

      checkConnection()
      const interval = setInterval(() => {
        if (isMounted.current) {
          checkConnection()
        }
      }, 20000)

      addTimeout(interval)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted.current) {
        retryCount.current = 0
        setupConnectionCheck()
      }
    }

    const handleOnline = () => {
      if (isMounted.current) {
        retryCount.current = 0
        checkConnection()
      }
    }

    setupConnectionCheck()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)

    return () => {
      isMounted.current = false
      clearTimeouts()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
      setIsLoading(false)
      setIsReconnecting(false)
    }
  }, [clearTimeouts, checkConnection, addTimeout])

  const handleRefresh = useCallback(() => {
    window.location.reload()
  }, [])

  return (
    <LoadingContext.Provider
      value={{ isLoading, setIsLoading, showLoader, hasConnectionError }}>
      <ErrorBoundary>
        {/* Loading Overlay */}
        {isLoading && (
          <div className="loading-overlay animate-in fade-in-0">
            <div className="loading-content flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Loading...</span>
            </div>
          </div>
        )}

        {/* Connection Error Alert */}
        {hasConnectionError && (
          <Alert
            variant="destructive"
            className="fixed top-4 right-4 z-[9999] max-w-md shadow-xl 
                     animate-in fade-in slide-in-from-top-2 
                     mobile-optimized-shadow">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                {isReconnecting
                  ? `Connection lost. Attempting to reconnect... (${retryCount.current}/${MAX_RETRIES})`
                  : retryCount.current >= MAX_RETRIES
                  ? 'Unable to connect to server. Please refresh the page to try again.'
                  : 'Connection error. Please check your internet connection.'}
              </span>
              {retryCount.current >= MAX_RETRIES && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  className="ml-2 hover:bg-destructive/10 active:bg-destructive/20">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        {children}
      </ErrorBoundary>
    </LoadingContext.Provider>
  )
}
